import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Loader2, PackageX, ArrowRight, MapPin, CalendarClock, User, Package, Camera } from "lucide-react";
import { API, fmtDay, fmtDate } from "@/lib/api";
import { TrackSearch } from "@/components/TrackSearch";
import { StatusBadge } from "@/components/StatusBadge";
import { TrackingMap } from "@/components/TrackingMap";
import { Timeline } from "@/components/Timeline";

const Card = ({ title, icon: Icon, children, testId }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid={testId}>
    <h3 className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-slate-500 mb-4"><Icon className="h-4 w-4 text-[#FF6B00]" />{title}</h3>
    {children}
  </div>
);

const Row = ({ k, v, testId }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0 text-sm">
    <span className="text-slate-500">{k}</span><span className="font-medium text-right text-[#0F172A]" data-testid={testId}>{v || "-"}</span>
  </div>
);

function Overview({ s }) {
  return (
    <div className="bg-[#0A192F] text-white rounded-sm p-6 md:p-8 relative overflow-hidden grain" data-testid="tracking-overview">
      <div className="relative flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div className="space-y-3">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Tracking number</p>
          <p className="font-mono font-bold tracking-wider text-2xl sm:text-3xl text-[#FF6B00]" data-testid="tracking-number">{s.tracking_number}</p>
          <StatusBadge status={s.status} testId="tracking-status" />
        </div>
        <div className="flex items-center gap-3 text-base sm:text-lg font-display font-semibold" data-testid="tracking-route">
          <span>{s.origin}</span><ArrowRight className="h-5 w-5 text-[#FF6B00] shrink-0" /><span>{s.destination}</span>
        </div>
      </div>
      <div className="relative grid sm:grid-cols-2 gap-4 mt-8 pt-6 border-t border-white/10 text-sm">
        <p className="flex items-center gap-2 text-slate-300"><MapPin className="h-4 w-4 text-[#FF6B00]" /> Current location: <span className="text-white font-medium" data-testid="tracking-current-location">{s.current_location?.name}</span></p>
        <p className="flex items-center gap-2 text-slate-300"><CalendarClock className="h-4 w-4 text-[#FF6B00]" /> Estimated delivery: <span className="text-white font-medium" data-testid="tracking-eta">{fmtDay(s.estimated_delivery)}</span></p>
      </div>
    </div>
  );
}

function Result({ s }) {
  const p = s.package || {};
  return (
    <div className="space-y-6">
      <Overview s={s} />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-slate-200 rounded-sm overflow-hidden bg-white"><TrackingMap shipment={s} /></div>
        <Card title="Shipment history" icon={CalendarClock} testId="timeline-card"><Timeline events={s.events} /></Card>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        <Card title="Sender" icon={User} testId="sender-card"><Row k="Name" v={s.sender.name} testId="sender-name" /><Row k="Address" v={s.sender.address} /></Card>
        <Card title="Receiver" icon={User} testId="receiver-card"><Row k="Name" v={s.receiver.name} testId="receiver-name" /><Row k="Address" v={s.receiver.address} /></Card>
        <Card title="Package" icon={Package} testId="package-card">
          <Row k="Contents" v={p.contents} testId="package-contents" />
          <Row k="Weight" v={p.weight_kg ? `${p.weight_kg} kg` : ""} testId="package-weight" />
          <Row k="Dimensions" v={p.dimensions} />
          <Row k="Quantity" v={p.quantity} />
          <Row k="Shipping method" v={p.service} testId="package-service" />
        </Card>
      </div>
      {s.proof_photo && (
        <Card title="Proof of delivery" icon={Camera} testId="proof-card">
          <a href={`${API}/proof/${s.proof_photo.id}`} target="_blank" rel="noreferrer" data-testid="proof-photo-link">
            <img src={`${API}/proof/${s.proof_photo.id}`} alt="Proof of delivery" className="w-full max-w-xl max-h-[420px] object-contain bg-slate-50 rounded-sm border" data-testid="proof-photo" />
          </a>
          <p className="text-xs font-mono text-slate-400 mt-2">Added {fmtDate(s.proof_photo.uploaded_at)}</p>
        </Card>
      )}
    </div>
  );
}

export default function TrackPage() {
  const { trackingNumber } = useParams();
  const q = useQuery({
    queryKey: ["track", trackingNumber],
    queryFn: async () => (await axios.get(`${API}/track/${encodeURIComponent(trackingNumber)}`)).data,
    enabled: !!trackingNumber, retry: false, staleTime: 0,
  });
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
      <div className="max-w-2xl space-y-4">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0A192F]">Track a consignment</h1>
        <TrackSearch key={trackingNumber} initial={trackingNumber || ""} />
      </div>
      {q.isLoading && <div className="flex items-center gap-2 text-slate-500" data-testid="tracking-loading"><Loader2 className="h-5 w-5 animate-spin" /> Locating your shipment...</div>}
      {q.isError && (
        <div className="bg-white border border-slate-200 rounded-sm p-10 text-center space-y-2" data-testid="tracking-not-found">
          <PackageX className="h-10 w-10 mx-auto text-slate-400" />
          <p className="font-display font-semibold text-lg">No shipment found</p>
          <p className="text-sm text-slate-500">Check the tracking number and try again.</p>
        </div>
      )}
      {q.data && <Result s={q.data} />}
    </div>
  );
}
